// src/features/commodities-info/api.ts
//
// Data fetcher for the Commodity Info page. Talks to the FastAPI
// commodities-info router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>) so the
//     auth-gated /api/v1/commodities-info router is reached as an authorized
//     user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/commodities-info/...` path here — adding
// `/api/v1` would double the prefix. See api/client.ts (REQUIRED_API_PREFIX).

import { apiClient } from '../../api/client';

// 1. TypeScript contract — mirrors the backend Pydantic `CommodityInfo`
//    (app/domain/commodities_info/schemas.py). Keep these in lock-step.
export interface CommodityInfo {
    commodity_symbol: string;
    commodity_name: string;
    description: string;
    how_to_trade_this_commodity_strategy: string[];
}

// Bare path — the shared apiClient baseURL already includes `/api/v1`.
const BASE_URL = '/commodities-info';

// 2. Fetcher: entire commodities metadata universe (served from the backend's
//    in-memory Polars cache).
export const fetchAllCommoditiesInfo = async (): Promise<CommodityInfo[]> => {
    const response = await apiClient.get<CommodityInfo[]>(BASE_URL);
    return response.data;
};

// 3. Fetcher: a single commodity by its pricing symbol (e.g. "GCUSD").
//    Case-insensitive on the backend; returns 404 for unknown symbols.
export const fetchCommodityInfoBySymbol = async (
    symbol: string,
): Promise<CommodityInfo> => {
    const response = await apiClient.get<CommodityInfo>(
        `${BASE_URL}/${encodeURIComponent(symbol)}`,
    );
    return response.data;
};

// 4. Live proxy-ETF quotes — mirrors the backend Pydantic `CommodityETF`.
//    Fields are already snake_case + volume-ranked server-side.
export interface CommodityETF {
    symbol: string;
    name: string | null;
    price: number | null;
    changes_percentage: number | null;
    volume: number | null;
}

// 5. Fetcher: up to 10 most-liquid proxy ETFs for a commodity symbol.
//    Backed by live FMP batch quotes; returns [] when no mapping exists.
export const fetchCommodityEtfs = async (
    symbol: string,
): Promise<CommodityETF[]> => {
    const response = await apiClient.get<CommodityETF[]>(
        `${BASE_URL}/${encodeURIComponent(symbol)}/etfs`,
    );
    return response.data;
};

// 6. Technical-indicator OHLCV bars for the Price/Volume candlestick chart.
//    GET /commodities-info/{symbol}/technical-indicators returns one OHLCV bar
//    per row PLUS the indicator value under a key named after the indicator
//    (sma/ema/wma/rsi/adx). FMP keys are already lowercase, so an index
//    signature captures whichever indicator column comes back on top of the
//    OHLCV fields.
export interface TechnicalIndicatorBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    // The active indicator arrives under its own key (e.g. `sma`, `rsi`).
    [indicator: string]: number | string;
}

// Valid indicators exposed by the backend proxy (mirrors the FMP
// /stable/technical-indicators/{indicator} slugs).
export type TechnicalIndicator = 'sma' | 'ema' | 'wma' | 'rsi' | 'adx';

export const fetchCommodityTechnicalIndicator = async (
    symbol: string,
    params: {
        indicator: TechnicalIndicator;
        periodLength: number;
        timeframe: string;
        from?: string;
        to?: string;
    },
): Promise<TechnicalIndicatorBar[]> => {
    const { data } = await apiClient.get<TechnicalIndicatorBar[]>(
        `${BASE_URL}/${encodeURIComponent(symbol)}/technical-indicators`,
        {
            // Backend aliases these to FMP's exact param names
            // (indicator / periodLength / timeframe / from / to).
            params: {
                indicator: params.indicator,
                periodLength: params.periodLength,
                timeframe: params.timeframe,
                ...(params.from ? { from: params.from } : {}),
                ...(params.to ? { to: params.to } : {}),
            },
        },
    );
    return data;
};
